<template>
  <div class="pm-cols pm-cols--even rp-schedule">
    <div class="rp-col">
      <!-- When rounds run -->
      <div class="pm-card" qid="recurring-schedule-card">
        <div class="pm-card__head">
          <h3>{{ $t('projects.tab.schedule') }}</h3>
          <span class="rp-inherit-tag"><font-awesome-icon icon="calendar-alt"/>{{ $t('projects.recurring.schedule.sub') }}</span>
          <span class="grow"></span>
          <span class="rp-icon-btn"
                v-if="canEdit && !editing"
                @click="startEditing"
                qid="recurring-schedule-edit"><font-awesome-icon icon="pen"/></span>
        </div>

        <div class="rp-schedule__view" v-if="!editing">
          <div class="rp-facts">
            <div class="rp-fact">
              <span>{{ $t('projects.launcher.how_often') }}</span>
              <b>{{ intervalText }}</b>
            </div>
            <div class="rp-fact">
              <span>{{ $t('projects.launcher.first_run') }}</span>
              <b>{{ project.scheduled && project.start_date ? formatDate(project.start_date) : '—' }}</b>
            </div>
            <div class="rp-fact">
              <span>{{ $t('projects.recurring.next_round') }}</span>
              <b :class="{'text-danger': project.run_due}">{{ nextText }}</b>
            </div>
            <div class="rp-fact">
              <span>{{ $t('projects.recurring.schedule.due_after') }}</span>
              <b>{{ periodText(project.due_after, project.due_after_interval) }}</b>
            </div>
            <div class="rp-fact" v-if="isAudit">
              <span>{{ $t('projects.recurring.schedule.data_due_after') }}</span>
              <b>{{ periodText(project.data_collection_due_after, project.data_collection_due_after_interval) }}</b>
            </div>
          </div>
          <div class="rp-auto-state" v-if="project.scheduled" :class="{'on': project.auto_create}">
            <span class="project-launcher__switch" :class="{'on': project.auto_create}"></span>
            <div>
              <b>{{ project.auto_create ? $t('projects.recurring.created_automatically') : $t('projects.recurring.started_by_hand') }}</b>
              <span>{{ project.auto_create ? $t('projects.recurring.auto_help') : $t('projects.recurring.manual_help') }}</span>
            </div>
          </div>
          <div class="rp-upcoming" v-if="upcoming.length">
            <span class="rp-upcoming__label">{{ $t('projects.recurring.schedule.upcoming') }}</span>
            <span class="rp-upcoming__date" v-for="(date, index) in upcoming" :key="index">{{ date.format('D MMM YYYY') }}</span>
          </div>
        </div>

        <!-- Same field designs as the Start project dialog -->
        <div class="rp-schedule__edit project-launcher" v-if="editing">
          <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
          <div class="project-launcher__field">
            <label>{{ $t('projects.launcher.how_often') }}</label>
            <div class="project-launcher__inline">
              <div class="project-launcher__control">
                <select v-model="form.option" class="project-launcher__bare-select" qid="recurring-schedule-option">
                  <option v-for="option in scheduleOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
                <font-awesome-icon icon="chevron-down" class="project-launcher__control-chevron"/>
              </div>
              <div class="project-launcher__control" v-if="form.option === 'custom'">
                <span class="project-launcher__control-label">{{ $t('projects.launcher.every') }}</span>
                <Stepper v-model="form.every"/>
                <select v-model="form.interval" class="project-launcher__bare-select">
                  <option v-for="option in intervalOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
                <font-awesome-icon icon="chevron-down" class="project-launcher__control-chevron"/>
              </div>
              <div class="project-launcher__control project-launcher__date" v-if="form.option !== 'none'">
                <font-awesome-icon icon="calendar-alt" class="project-launcher__control-icon"/>
                <span class="project-launcher__control-label">{{ $t('projects.launcher.first_run') }}</span>
                <datepicker v-model="form.start_date"
                            format="d MMM yyyy"
                            :monday-first="true"
                            calendar-class="project-launcher__calendar"
                            qid="recurring-schedule-first-run"></datepicker>
              </div>
            </div>
            <div class="project-launcher__help" v-if="form.option === 'none'">{{ $t('projects.launcher.repeats_unscheduled_help') }}</div>
          </div>

          <div class="project-launcher__auto"
               v-if="form.option !== 'none'"
               @click="form.auto_create = !form.auto_create"
               qid="recurring-schedule-auto">
            <span class="project-launcher__switch" :class="{'on': form.auto_create}"></span>
            <div>
              <b>{{ $t('projects.launcher.auto_create_title') }}</b>
              <span>{{ $t('projects.launcher.auto_create_help') }}</span>
            </div>
          </div>

          <div class="project-launcher__field" v-if="form.option !== 'none'">
            <label>{{ $t('projects.recurring.schedule.due_after') }}</label>
            <div class="project-launcher__inline">
              <div class="project-launcher__control">
                <Stepper v-model="form.due_after"/>
                <select v-model="form.due_after_interval" class="project-launcher__bare-select">
                  <option v-for="option in intervalOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
                <font-awesome-icon icon="chevron-down" class="project-launcher__control-chevron"/>
              </div>
              <div class="project-launcher__control" v-if="isAudit">
                <span class="project-launcher__control-label">{{ $t('projects.recurring.schedule.data_due_after') }}</span>
                <Stepper v-model="form.data_collection_due_after"/>
                <select v-model="form.data_collection_due_after_interval" class="project-launcher__bare-select">
                  <option v-for="option in intervalOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
                <font-awesome-icon icon="chevron-down" class="project-launcher__control-chevron"/>
              </div>
            </div>
            <div class="project-launcher__help">{{ $t('projects.recurring.schedule.due_after_help') }}</div>
          </div>

          <div class="rp-form-actions">
            <b-button variant="secondary" @click="editing = false" qid="recurring-schedule-cancel">{{ $t('projects.create.cancel') }}</b-button>
            <b-button variant="success"
                      class="ml-2"
                      :disabled="saving"
                      @click="saveSchedule"
                      qid="recurring-schedule-save">
              <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>{{ $t('tasks.modal.save') }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Description: copied into every round -->
      <div class="pm-card" qid="recurring-description-card" v-if="!isAssessment">
        <div class="pm-card__head">
          <h3>{{ $t('activities.description_label') }}</h3>
          <span class="rp-inherit-tag"><font-awesome-icon icon="arrow-down"/>{{ $t('projects.recurring.inherited') }}</span>
          <span class="grow"></span>
          <span class="rp-icon-btn"
                v-if="canEdit && !editingDescription"
                @click="editDescription"
                qid="recurring-description-edit"><font-awesome-icon icon="pen"/></span>
        </div>
        <div class="rp-card-body">
          <template v-if="editingDescription">
            <RichTextEditor v-model="description"
                            :rows="6"
                            :headings="false"
                            compact
                            :placeholder="$t('maintenance.third_parties.create.description_placeholder')"/>
            <div class="rp-form-actions">
              <b-button variant="secondary" @click="editingDescription = false">{{ $t('projects.create.cancel') }}</b-button>
              <b-button variant="success"
                        class="ml-2"
                        :disabled="saving"
                        @click="save({description: description}, () => { editingDescription = false })"
                        qid="recurring-description-save">{{ $t('tasks.modal.save') }}</b-button>
            </div>
          </template>
          <RichTextView v-else-if="project.description"
                        :value="project.description"
                        :headings="false"
                        compact
                        class="text-formatted"/>
          <span class="text-muted" v-else>{{ $t('system.missing_input') }}</span>
        </div>
      </div>
    </div>

    <div class="rp-col">
      <!-- Description: copied into every round -->
      <div class="pm-card" qid="recurring-description-card" v-if="isAssessment">
        <div class="pm-card__head">
          <h3>{{ $t('activities.description_label') }}</h3>
          <span class="rp-inherit-tag"><font-awesome-icon icon="arrow-down"/>{{ $t('projects.recurring.inherited') }}</span>
          <span class="grow"></span>
          <span class="rp-icon-btn"
                v-if="canEdit && !editingDescription"
                @click="editDescription"
                qid="recurring-description-edit"><font-awesome-icon icon="pen"/></span>
        </div>
        <div class="rp-card-body">
          <template v-if="editingDescription">
            <RichTextEditor v-model="description"
                            :rows="6"
                            :headings="false"
                            compact
                            :placeholder="$t('maintenance.third_parties.create.description_placeholder')"/>
            <div class="rp-form-actions">
              <b-button variant="secondary" @click="editingDescription = false">{{ $t('projects.create.cancel') }}</b-button>
              <b-button variant="success"
                        class="ml-2"
                        :disabled="saving"
                        @click="save({description: description}, () => { editingDescription = false })"
                        qid="recurring-description-save">{{ $t('tasks.modal.save') }}</b-button>
            </div>
          </template>
          <RichTextView v-else-if="project.description"
                        :value="project.description"
                        :headings="false"
                        compact
                        class="text-formatted"/>
          <span class="text-muted" v-else>{{ $t('system.missing_input') }}</span>
        </div>
      </div>

      <!-- Message to the people who get tasks in a round -->
      <div class="pm-card" qid="recurring-message-card" v-if="!isAssessment">
        <div class="pm-card__head">
          <h3>{{ $t('projects.recurring.cards.message') }}</h3>
          <span class="rp-inherit-tag"><font-awesome-icon icon="arrow-down"/>{{ $t('projects.recurring.inherited') }}</span>
          <span class="grow"></span>
          <span class="rp-icon-btn"
                v-if="canEdit && !editingMessage"
                @click="editMessage"
                qid="recurring-message-edit"><font-awesome-icon icon="pen"/></span>
        </div>
        <div class="rp-card-body">
          <template v-if="editingMessage">
            <b-form-textarea v-model="message"
                             rows="5"
                             max-rows="10"
                             :placeholder="$t('projects.create.message_placeholder')"
                             qid="recurring-message-input"></b-form-textarea>
            <div class="rp-form-actions">
              <b-button variant="secondary" @click="editingMessage = false">{{ $t('projects.create.cancel') }}</b-button>
              <b-button variant="success"
                        class="ml-2"
                        :disabled="saving"
                        @click="save({schedule_message: message}, () => { editingMessage = false })"
                        qid="recurring-message-save">{{ $t('tasks.modal.save') }}</b-button>
            </div>
          </template>
          <div class="rp-quote" v-else-if="project.schedule_message">{{ project.schedule_message }}</div>
          <span class="text-muted" v-else>{{ $t('system.missing_input') }}</span>
        </div>
      </div>

      <!-- Files and links copied into a round when "keep attachments" is on -->
      <div class="pm-card" qid="recurring-attachments-card" v-if="!isAssessment">
        <div class="pm-card__head">
          <h3>{{ $t('projects.recurring.cards.attachments') }}</h3>
          <span class="rp-inherit-tag"><font-awesome-icon icon="arrow-down"/>{{ $t('projects.recurring.inherited') }}</span>
        </div>
        <div class="rp-card-body">
          <b-alert v-if="attachmentError" show variant="danger">{{ attachmentError }}</b-alert>
          <div class="rp-files" v-if="files.length || links.length">
            <div class="rp-file" v-for="file in files" :key="'f' + file.id">
              <span class="pm-icon pm-icon--sm pm-c-grey"><font-awesome-icon icon="file"/></span>
              <a class="grow" @click="downloadMedia(file)">{{ file.file_name }}</a>
              <span class="pm-row-action" v-if="canEdit" @click="removeFile(file)"><font-awesome-icon icon="trash-alt"/></span>
            </div>
            <div class="rp-file" v-for="link in links" :key="'l' + link.id">
              <span class="pm-icon pm-icon--sm pm-c-blue"><font-awesome-icon icon="link"/></span>
              <a class="grow" :href="link.url" target="_blank">{{ link.name || link.url }}</a>
              <span class="pm-row-action" v-if="canEdit" @click="removeLink(link)"><font-awesome-icon icon="trash-alt"/></span>
            </div>
          </div>
          <div class="text-muted" v-else>{{ $t('projects.recurring.schedule.no_attachments') }}</div>

          <div class="rp-attach-actions" v-if="canEdit">
            <label class="rp-attach-btn" qid="recurring-upload">
              <input type="file" multiple @change="onFileChange">
              <font-awesome-icon icon="cloud-upload-alt"/>{{ $t('attachments.upload_button') }}
            </label>
            <span class="rp-attach-btn" @click="showLinkInput = !showLinkInput" qid="recurring-add-link">
              <font-awesome-icon icon="link"/>{{ $t('projects.recurring.schedule.add_link') }}
            </span>
          </div>
          <div class="rp-link-form" v-if="showLinkInput">
            <b-form-input v-model="inputLink" :placeholder="$t('maintenance.third_parties.create.links_placeholder')"></b-form-input>
            <b-form-input v-model="inputLinkName" :placeholder="inputLink || $t('table.name')"></b-form-input>
            <b-button variant="success"
                      :disabled="!inputLink"
                      @click="addLink"
                      qid="recurring-link-save">{{ $t('tasks.modal.save') }}</b-button>
          </div>
          <div class="project-launcher__help">{{ $t('projects.recurring.schedule.attachments_help') }}</div>
        </div>
      </div>
    </div>

    <ConfirmRemoveAttachment :object="selectedFile"
                             :show="showDeleteFile"
                             type="file"
                             @closeConfirmModal="attachmentRemoved"></ConfirmRemoveAttachment>
    <ConfirmRemoveAttachment :object="selectedLink"
                             :show="showDeleteLink"
                             type="link"
                             @closeConfirmModal="attachmentRemoved"></ConfirmRemoveAttachment>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Datepicker from 'vuejs-datepicker';
  import RichTextEditor from '@/components/richText/RichTextEditor.vue';
  import RichTextView from '@/components/richText/RichTextView.vue';
  import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
  import { runDates, nextRunDate } from '@/views/Projects/Management/projectTypes.js'

  const PRESETS = {
    month: {every: 1, interval: 'Months'},
    quarter: {every: 3, interval: 'Months'},
    half_year: {every: 6, interval: 'Months'},
    year: {every: 1, interval: 'Years'}
  };

  // Number field with − and + in the launcher's design
  const Stepper = {
    name: 'Stepper',
    props: ['value'],
    render(h) {
      let set = (value) => this.$emit('input', Math.max(1, parseInt(value, 10) || 1));
      return h('span', {class: 'project-launcher__stepper'}, [
        h('span', {on: {click: () => set((parseInt(this.value, 10) || 1) - 1)}}, [h('font-awesome-icon', {props: {icon: 'minus'}})]),
        h('input', {attrs: {type: 'number', min: 1}, domProps: {value: this.value}, on: {input: (event) => set(event.target.value)}}),
        h('span', {on: {click: () => set((parseInt(this.value, 10) || 0) + 1)}}, [h('font-awesome-icon', {props: {icon: 'plus'}})])
      ]);
    }
  };

  // Schedule tab of a recurring project: when rounds run, and the description, message and attachments they get (US 9396)
  export default {
    name: 'RecurringSchedule',
    components: {
      Datepicker,
      RichTextEditor,
      RichTextView,
      ConfirmRemoveAttachment,
      Stepper
    },
    props: {
      project: {type: Object, required: true},
      canEdit: {type: Boolean, default: false}
    },
    data: function() {
      return {
        editing: false,
        editingDescription: false,
        editingMessage: false,
        saving: false,
        error: '',
        attachmentError: '',
        form: {},
        description: '',
        message: '',
        showLinkInput: false,
        inputLink: '',
        inputLinkName: '',
        selectedFile: {},
        selectedLink: {},
        showDeleteFile: false,
        showDeleteLink: false
      }
    },
    computed: {
      isAudit() {
        return this.project.type === 'control_assessment';
      },
      // Assessments don't send a message or attachments to assignees, so those cards are left out
      isAssessment() {
        return ['control_assessment', 'risk_assessment', 'bia_assessment'].indexOf(this.project.type) !== -1;
      },
      scheduleOptions() {
        return ['none', 'month', 'quarter', 'half_year', 'year', 'custom'].map((value) => {
          return {value: value, text: this.$t('projects.launcher.schedule.' + value)};
        });
      },
      intervalOptions() {
        return ['Days', 'Months', 'Years'].map((value) => {
          return {value: value, text: this.$t('system.interval_' + value.toLowerCase())};
        });
      },
      intervalText() {
        if (!this.project.scheduled || !this.project.repeat_after) {
          return this.$t('projects.launcher.schedule.none');
        }
        let preset = _.findKey(PRESETS, {every: parseInt(this.project.repeat_after, 10), interval: this.project.repeat_after_interval});
        if (preset) {
          return this.$t('projects.launcher.schedule.' + preset);
        }
        return this.$t('projects.launcher.every') + ' ' + this.periodText(this.project.repeat_after, this.project.repeat_after_interval);
      },
      nextText() {
        if (this.project.run_due) {
          return this.$t('projects.launcher.due_now');
        }
        let next = nextRunDate(this.project);
        return next ? next.format('D MMM YYYY') : '—';
      },
      upcoming() {
        var moment = require('moment');
        return runDates(this.project, moment().add(1, 'day'), 4);
      },
      files() {
        return _.filter(this.project.media || [], function(media) {
          return media.collection_name === 'documents' || media.collection_name === 'project';
        });
      },
      links() {
        return _.filter(this.project.links || [], function(link) {
          return link.collection == null;
        });
      }
    },
    methods: {
      formatDate(value) {
        var moment = require('moment');
        return moment(value.substring(0, 10)).format('D MMM YYYY');
      },
      periodText(every, interval) {
        if (!every || !interval) {
          return '—';
        }
        return every + ' ' + this.$t('system.interval_' + interval.toLowerCase()).toLowerCase();
      },
      startEditing() {
        var moment = require('moment');
        let project = this.project;
        let option = 'none';
        if (project.scheduled && project.repeat_after) {
          option = _.findKey(PRESETS, {every: parseInt(project.repeat_after, 10), interval: project.repeat_after_interval}) || 'custom';
        }
        this.form = {
          option: option,
          every: parseInt(project.repeat_after, 10) || 1,
          interval: project.repeat_after_interval || 'Years',
          start_date: project.start_date ? moment(project.start_date.substring(0, 10)).toDate() : moment().add(1, 'day').toDate(),
          auto_create: !!project.auto_create,
          due_after: parseInt(project.due_after, 10) || 14,
          due_after_interval: project.due_after_interval || 'Days',
          data_collection_due_after: parseInt(project.data_collection_due_after, 10) || 14,
          data_collection_due_after_interval: project.data_collection_due_after_interval || 'Days'
        };
        this.error = '';
        this.editing = true;
      },
      saveSchedule() {
        var moment = require('moment');
        let form = this.form;
        if (form.option === 'none') {
          this.save({scheduled: 0, auto_create: 0}, () => { this.editing = false; });
          return;
        }
        let repeat = form.option === 'custom' ? {every: form.every, interval: form.interval} : PRESETS[form.option];
        if (!form.start_date) {
          this.error = this.$t('projects.schedule.start_date_error');
          return;
        }
        if (form.due_after_interval === 'Days' && form.due_after > 365) {
          this.error = this.$t('projects.schedule.max_due_after_error');
          return;
        }
        let data = {
          scheduled: 1,
          repeat_after: repeat.every,
          repeat_after_interval: repeat.interval,
          auto_create: form.auto_create ? 1 : 0,
          due_after: form.due_after,
          due_after_interval: form.due_after_interval
        };
        if (this.isAudit) {
          data.data_collection_due_after = form.data_collection_due_after;
          data.data_collection_due_after_interval = form.data_collection_due_after_interval;
        }
        // Only a changed first run date is sent, as saving a start date can start a project
        let startDate = moment(form.start_date).format('YYYY-MM-DD');
        if (!this.project.start_date || this.project.start_date.substring(0, 10) !== startDate) {
          data.start_date = startDate;
        }
        this.save(data, () => { this.editing = false; });
      },
      editDescription() {
        this.description = this.project.description || '';
        this.editingDescription = true;
      },
      editMessage() {
        this.message = this.project.schedule_message || '';
        this.editingMessage = true;
      },
      save(data, done) {
        this.saving = true;
        piincHttp.put('projects/' + this.project.slug, data).then((response) => {
          this.saving = false;
          this.$toastr('success', this.$t('projects.update.success'));
          done();
          this.$emit('updated', response.data);
        }, (error) => {
          this.saving = false;
          this.error = error.data && error.data.message ? error.data.message : this.$t('projects.launcher.error_generic');
        });
      },
      onFileChange(event) {
        let files = event.target.files;
        let formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          // Equal to 10 MB
          if (files[i].size > 10485760) {
            this.attachmentError = this.$t('system.file_size');
            return;
          }
          formData.append('document[]', files[i]);
        }
        formData.append('name', 'documents');
        formData.append('model_type', this.project.who_am_i);
        formData.append('model_id', this.project.id);
        piincHttp.post('documents', formData, {headers: {'Content-Type': 'multipart/form-data'}}).then(() => {
          event.target.value = '';
          this.attachmentError = '';
          this.$toastr('success', this.$t('maintenance.third_parties.media_add_success'));
          this.$emit('updated');
        }, (error) => {
          this.attachmentError = error.data && error.data.message ? error.data.message : this.$t('projects.launcher.error_generic');
        });
      },
      addLink() {
        let url = this.inputLink.startsWith('http') ? this.inputLink : 'http://' + this.inputLink;
        let formData = new FormData();
        formData.append('url', url);
        formData.append('name', this.inputLinkName);
        formData.append('model_type', this.project.who_am_i);
        formData.append('model_id', this.project.id);
        piincHttp.post('links', formData).then(() => {
          this.inputLink = '';
          this.inputLinkName = '';
          this.showLinkInput = false;
          this.$toastr('success', this.$t('maintenance.third_parties.link_add_success'));
          this.$emit('updated');
        }, () => {});
      },
      downloadMedia(file) {
        piincHttp.get('documents/' + file.id + '/download', {responseType: 'blob'}).then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = file.file_name;
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }, () => {});
      },
      removeFile(file) {
        this.selectedFile = file;
        this.showDeleteFile = true;
      },
      removeLink(link) {
        this.selectedLink = link;
        this.showDeleteLink = true;
      },
      attachmentRemoved() {
        this.showDeleteFile = false;
        this.showDeleteLink = false;
        this.selectedFile = {};
        this.selectedLink = {};
        this.$emit('updated');
      }
    }
  }
</script>
